import { Fragment } from 'react';
import type { ShippingPosition } from './types';

interface ShippingPositionsProps {
  /** Cost lines (shipping fee, sales tax, handling fee, …). */
  positions: ShippingPosition[];
  /** Optional emphasised final row (Total). Rendered last, after a
   *  hairline divider, with a heavier value — Figma 703:28734. */
  total?: ShippingPosition;
}

/**
 * The fees breakdown mini-card of `ShippingModal` — Figma 703:28734.
 * A list of "positions" (label / value rows) separated by hairline
 * dividers, with an optional emphasised Total row at the bottom.
 *
 * Fully data-driven: the reveal flow passes 3 positions + a Total; the
 * My Packs ship flow passes 2 positions and no Total. Rendered only when
 * `positions` is non-empty.
 *
 * Tagged `data-co-component="ShippingModal"` so a right-click here opens
 * the same single Design Constructor panel as the rest of the modal.
 * Colour fallbacks go through `--ship-default-*`, which only sleek's dark
 * sheet sets; elsewhere they resolve to the light values.
 */
export function ShippingPositions({ positions, total }: ShippingPositionsProps) {
  const rows = total ? [...positions, total] : positions;
  const totalIndex = total ? rows.length - 1 : -1;

  return (
    <div
      data-co-component="ShippingModal"
      className="rounded-[18px] border px-5"
      style={{
        borderColor: 'var(--co-ShippingModal-feeCardBorder, var(--ship-default-feeCardBorder, rgba(0,0,0,0.08)))',
        backgroundColor: 'var(--co-ShippingModal-feeCardFill, var(--ship-default-feeCardFill, #FFFFFF))',
      }}
    >
      {rows.map((row, i) => (
        <Fragment key={`${row.label}-${i}`}>
          {i > 0 && <Divider />}
          <Row
            label={row.label}
            value={row.value}
            strong={i === totalIndex}
            muted={row.muted ?? false}
          />
        </Fragment>
      ))}
    </div>
  );
}

function Row({ label, value, strong, muted }: ShippingPosition & { strong: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-[14px]">
      <span
        className="shrink-0"
        style={{
          color: 'var(--co-ShippingModal-rowLabelColor, var(--ship-default-rowLabelColor, rgba(0,0,0,0.55)))',
          fontFamily: 'var(--co-ShippingModal-rowFont, "Inter"), Inter, sans-serif',
          fontSize: 'var(--co-ShippingModal-rowLabelSize, 17px)',
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
      <span
        className={`text-right tabular-nums ${
          muted ? 'font-normal' : strong ? 'font-bold' : 'font-semibold'
        }`}
        style={{
          color: muted
            ? 'var(--co-ShippingModal-rowLabelColor, var(--ship-default-rowLabelColor, rgba(0,0,0,0.55)))'
            : 'var(--co-ShippingModal-rowValueColor, var(--ship-default-rowValueColor, #15181E))',
          fontFamily: 'var(--co-ShippingModal-rowFont, "Inter"), Inter, sans-serif',
          fontSize: 'var(--co-ShippingModal-rowValueSize, 17px)',
          lineHeight: 1.3,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return (
    <div
      className="h-px w-full"
      style={{ backgroundColor: 'var(--co-ShippingModal-rowDivider, var(--ship-default-rowDivider, rgba(0,0,0,0.06)))' }}
    />
  );
}
